import React from 'react';
import * as yup from 'yup';
import { parse } from 'csv-parse/lib/sync';
import { AnnotationStandardDetails, FindingTypeTemplateDetails } from '../../AnnotationManager/AnnotationStandardForm/AnnotationStandardForm.slice';
import { ResponseLevel } from './MaintenanceTriggerForm.slice';
import confirm from 'antd/lib/modal/confirm';

type MaintenanceTriggerRaw = {
  'Finding Type': string;
  'Blade Zone': string;
  Layer: string;
  Severity: string;
  Observation: string;
  Subcategory: string;
  'Abs. Longitudinal Length [mm]': number | undefined;
  'Abs. Transverse Length [mm]': number | undefined;
  'Rel. Length per [m] %': number | undefined;
  'Rel. Area per [m2] %': number | undefined;
  'Abs. Length [mm]': number | undefined;
  'Abs. Area [m2]': number | undefined;
  'Response Level (Code)': string;
};

type AnnotationStandardImportRuleRaw = {
  'Finding Type': string | undefined;
  'Blade Zone': string | undefined;	
  Layer: string | undefined;	
  Severity: string | undefined;	
  Subcategory: string | undefined;	
  Observation: string | undefined;
}

export type AnnotationStandardImportRule = {
  findingType: string;
  bladeZone: string | undefined;	
  layer: string | undefined;	
  severity: string | undefined;	
  subcategory: string | undefined;	
  observation: string | undefined;
  [key: string]: string | undefined;
}

export type MaintenanceTrigger = {
  FindingType: string;
  BladeZone: string;
  Layer: string;
  Severity: string;
  Observation: string;
  Subcategory: string;
  AbsLongitudinalLengthMM: number | undefined;
  AbsTransverseLengthMM: number | undefined;
  RelLengthPerM: number | undefined;
  RelAreaPerM2: number | undefined;
  AbsLengthMM: number | undefined;
  AbsAreaM2: number | undefined;
  ResponseLevel: string;
  ResponseLevelDescription?: string;
};

export const validateAnnotationStandardImportRuleRow = (
  rows: AnnotationStandardImportRule[],
  findingTypes: Array<FindingTypeTemplateDetails>
) => {
  const findingTypeNames = findingTypes.map(x => x.name);
  const severityValues = [...new Set(findingTypes.map(x => x.properties.find(y => y.name === 'Severity')?.values.split(',').map(z => z.trim())).flat().filter(x => x)), ''];
  const layerValues = [...new Set(findingTypes.map(x => x.properties.find(y => y.name === 'Layer')?.values.split(',').map(z => z.trim())).flat().filter(x => x)), ''];
  const bladeZoneValues = [...new Set(findingTypes.map(x => x.properties.find(y => y.name === 'BladeZone')?.values.split(',').map(z => z.trim())).flat().filter(x => x)), ''];
  const subcategoryValues = [...new Set(findingTypes.map(x => x.properties.find(y => y.name === 'Subcategory')?.values.split(',').map(z => z.trim())).flat().filter(x => x)), ''];
  const observationValues = [...new Set(findingTypes.map(x => x.properties.find(y => y.name === 'Observation')?.values.split(',').map(z => z.trim())).flat().filter(x => x)), ''];

  const annotationStandardImportRuleSchema = yup.array().of(
    yup.object().shape({
      findingType: yup.string().required().oneOf(findingTypeNames),
      bladeZone: yup.string().optional().oneOf(bladeZoneValues),
      layer: yup.string().optional().oneOf(layerValues),
      severity: yup.string().optional().oneOf(severityValues),
      subcategory: subcategoryValues.length > 1 ? 
        yup.string().optional().oneOf(subcategoryValues) :
        yup.string().optional(),
      observation: observationValues.length > 1 ? 
        yup.string().optional().oneOf(observationValues) :
        yup.string().optional()
    })
  );

  try {
    let result = annotationStandardImportRuleSchema.validateSync(rows, { abortEarly: false });
  } catch (error: yup.ValidationError | any) {
    //left with purpose :)
    console.log('error', error);
    console.log('inner', error?.inner);

    confirm({
      content: (
        <div>
          <h5>Invalid CSV file. Please check the file and try again</h5>
          {error?.inner?.map((x: any) => (
            <div>{x.message}, and the value was: {x.value}</div>
          ))}
        </div>
      ),
      okCancel: false
    });
    throw new Error('error ocurred');
  }
}

export const validateMaintenanceTriggerRawRow = (
  rows: MaintenanceTrigger[],
  standard: AnnotationStandardDetails,
  responseLevels: ResponseLevel[]
) => {
  const findingTypes = standard.findingTypes.map(x => x.findingTypeTemplate?.name!);
  const responseLevelCodes = responseLevels.map(x => x.code);

  const maintenanceTriggerSchema = yup.array().of(
    yup.object().shape({
      FindingType: yup.string().required().oneOf(findingTypes),
      Layer: yup.string().when('FindingType', (value, schema) => {
        const findingType = standard.findingTypes.find(x => x.findingTypeTemplate?.name === value[0]?.trim());
        const layer = findingType?.findingTypeTemplate?.properties.find(y => y.name === 'Layer');
        if (layer) {
          return schema.required().oneOf(layer.values.split(',').map(x => x.trim()));
        }

        return schema.notRequired();
      }),
      Severity: yup.string().when('FindingType', (value, schema) => {
        const findingType = standard.findingTypes.find(x => x.findingTypeTemplate?.name === value[0]?.trim());
        const severity = findingType?.findingTypeTemplate?.properties.find(y => y.name === 'Severity');
        if (severity) {
          return schema.required().oneOf(severity.values.split(',').map(x => x.trim()));
        }

        return schema.notRequired();
      }),
      BladeZone: yup.string().when('FindingType', (value, schema) => {
        const findingType = standard.findingTypes.find(x => x.findingTypeTemplate?.name === value[0]?.trim());
        const bladeZone = findingType?.findingTypeTemplate?.properties.find(y => y.name === 'BladeZone');
        if (bladeZone) {
          return schema.required().oneOf(bladeZone.values.split(',').map(x => x.trim()));
        }

        return schema.notRequired();
      }),
      Observation: yup.string().when('FindingType', (value, schema) => {
        const findingType = standard.findingTypes.find(x => x.findingTypeTemplate?.name === value[0]?.trim());
        const observation = findingType?.findingTypeTemplate?.properties.find(y => y.name === 'Observation');
        if (observation) {
          return schema.required().oneOf(observation.values.split(',').map(x => x.trim()));
        }

        return schema.notRequired();
      }),
      Subcategory: yup.string().when('FindingType', (value, schema) => {
        const findingType = standard.findingTypes.find(x => x.findingTypeTemplate?.name === value[0]?.trim());
        const subcategory = findingType?.findingTypeTemplate?.properties.find(y => y.name === 'Subcategory');
        if (subcategory) {
          return schema.required().oneOf(subcategory.values.split(',').map(x => x.trim()));
        }

        return schema.notRequired();
      }),
      AbsLongitudinalLengthMM: yup
        .number()
        .optional()
        .nullable()
        .transform(value => (Number.isNaN(value) ? null : value)),
      AbsTransverseLengthMM: yup
        .number()
        .optional()
        .nullable()
        .transform(value => (Number.isNaN(value) ? null : value)),
      RelLengthPerM: yup
        .number()
        .optional()
        .nullable()
        .transform(value => (Number.isNaN(value) ? null : value)),
      RelAreaPerM2: yup
        .number()
        .optional()
        .nullable()
        .transform(value => (Number.isNaN(value) ? null : value)),
      AbsLengthMM: yup
        .number()
        .optional()
        .nullable()
        .transform(value => (Number.isNaN(value) ? null : value)),
      AbsAreaM2: yup
        .number()
        .optional()
        .nullable()
        .transform(value => (Number.isNaN(value) ? null : value)),
      ResponseLevel: yup.string().required().oneOf(responseLevelCodes)
    })
  );

  try {
    let result = maintenanceTriggerSchema.validateSync(rows, { abortEarly: false });
  } catch (error: yup.ValidationError | any) {
    //left with purpose :)
    console.log('error', error);
    console.log('inner', error?.inner);

    confirm({
      content: (
        <div>
          <h5>Invalid CSV file. Please check the file and try again</h5>
          {error?.inner?.map((x: any) => (
            <div>{x.message}</div>
          ))}
        </div>
      ),
      okCancel: false
    });
    throw new Error('error ocurred');
  }
};

export const parseCSV = (file: File): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const csvData = reader.result as string;

      const data = parse(csvData, {
        columns: true,
        skip_empty_lines: true,
        delimiter: '\t' // Specify tab as the delimiter
      });      

      resolve(data);
    };

    reader.onerror = err => {
      reject(err);
    };

    reader.readAsText(file);
  });
};

export const mapMaintenanceTriggers = (data: any[]): MaintenanceTrigger[] => {
  return data.map((row: MaintenanceTriggerRaw) => {
    return mapMaintenanceTrigger(row);
  });
}

export const mapAnnotationStandardImportRules = (data: any[]): AnnotationStandardImportRule[] => {
  return data.map((row: AnnotationStandardImportRuleRaw) => {
    return mapAnnotationStandardImportRule(row);
  });
}

const mapMaintenanceTrigger = (trigger: MaintenanceTriggerRaw): MaintenanceTrigger => {
  return {
    FindingType: trigger['Finding Type']?.trim(),
    BladeZone: trigger['Blade Zone']?.trim(),
    Layer: trigger.Layer?.trim(),
    Severity: trigger.Severity?.trim(),
    Observation: trigger.Observation?.trim(),
    Subcategory: trigger.Subcategory?.trim(),
    AbsLongitudinalLengthMM: trigger['Abs. Longitudinal Length [mm]'],
    AbsTransverseLengthMM: trigger['Abs. Transverse Length [mm]'],
    RelLengthPerM: trigger['Rel. Length per [m] %'],
    RelAreaPerM2: trigger['Rel. Area per [m2] %'],
    AbsLengthMM: trigger['Abs. Length [mm]'],
    AbsAreaM2: trigger['Abs. Area [m2]'],
    ResponseLevel: trigger['Response Level (Code)']?.trim()
  };
};

const mapAnnotationStandardImportRule = (rule: AnnotationStandardImportRuleRaw): AnnotationStandardImportRule => {
  return {
    findingType: rule['Finding Type']!.trim(),
    bladeZone: rule['Blade Zone']?.trim(),
    layer: rule.Layer?.trim(),
    severity: rule.Severity?.trim(),
    subcategory: rule.Subcategory?.trim(),
    observation: rule.Observation?.trim()
  };
};

export const propertyNameToRuleKey = (propertyName: string, rule: AnnotationStandardImportRule): string => {
  switch (propertyName) {
    case 'Layer':
      return rule.layer ?? '';
    case 'Severity':
      return rule.severity ?? '';
    case 'Observation':
      return rule.observation ?? '';
    case 'BladeZone':
      return rule.bladeZone ?? '';
    case 'Subcategory':
      return rule.subcategory ?? '';
    default:
      return '';
  }
}

export const mapMaintenaceTriggerToRule = (data: MaintenanceTrigger[], rulesCount: number): any => {
  return data.map((element, index) => {
    return {
      AbsAreaM2: element.AbsAreaM2 || '',
      AbsLengthMM: element.AbsLengthMM || '',
      AbsLongitudinalLengthMM: element.AbsLongitudinalLengthMM || '',
      AbsTransverseLengthMM: element.AbsTransverseLengthMM || '',
      Layer: element.Layer,
      RelAreaPerM2: element.RelAreaPerM2 || '',
      RelLengthPerM: element.RelLengthPerM || '',
      Severity: element.Severity,
      Observation: element.Observation,
      Subcategory: element.Subcategory,
      comments: '',
      defectLocation: element.BladeZone,
      findingType: element.FindingType,
      index: index + rulesCount + 1,
      responseLevel: element.ResponseLevel,
      responseLevelDescription: element.ResponseLevelDescription
    };
  });
};
